import React, { useState, useEffect } from 'react';
import { Drawer, Form, Input, Button, Select, Popconfirm, message, Space, Divider } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { tc } from '../../../i18n/common';

const DepartmentEditDrawer = ({
    visible,
    onClose,
    node,
    allUsers,
    locale,
    onSaveOrg,
    onAddUser,
    onUpdateUser,
    onRemoveUser,
    checkUniqueOrgExtId,
    checkUniqueUserExtId,
    checkUniqueEmail,
    onAddChildOrg,
    onDeleteOrg,
    forceEditUser
}) => {
    // Internal UI State
    const [mode, setMode] = useState('EDIT_ORG'); // 'EDIT_ORG', 'ADD_USER', 'EDIT_USER'
    const [editingUser, setEditingUser] = useState(null);
    const [targetRole, setTargetRole] = useState('mitarbeiter');
    const [addUserType, setAddUserType] = useState('new');

    const [form] = Form.useForm();
    const [userForm] = Form.useForm();

    // Reset state and handle external force mode
    useEffect(() => {
        if (visible) {
            if (forceEditUser) {
                 setMode('EDIT_USER');
                 setEditingUser(forceEditUser);
                 userForm.setFieldsValue(forceEditUser);
            } else {
                 setMode('EDIT_ORG');
                 setEditingUser(null);
            }
            if (!forceEditUser) form.resetFields(); // Only reset org form if not forcing user edit
        }
    }, [visible, node, form, forceEditUser]);

    // Handle User Edit Click
    const handleEditUserClick = (user) => {
        setEditingUser(user);
        setMode('EDIT_USER');
        userForm.setFieldsValue(user);
    };

    // Handle Add User Click
    const handleAddUserClick = (role) => {
        setTargetRole(role);
        setMode('ADD_USER');
        setAddUserType('new');
        // Clear any leftover user from a previous edit: the email/external-ID
        // uniqueness validators exempt editingUser, so a stale value lets the
        // previous user's email slip through as a "unique" duplicate.
        setEditingUser(null);
        userForm.resetFields();
    };

    // Confirm User Form (Add or Edit)
    const handleUserSubmit = (values) => {
        if (mode === 'ADD_USER') {
            onAddUser({ ...values, type: addUserType }, targetRole);
        } else {
            onUpdateUser({ ...editingUser, ...values });
        }
        setMode('EDIT_ORG');
        userForm.resetFields();
    };

    const drawerTitle = () => {
        if (mode === 'EDIT_ORG') return tc('organigram_ui.edit_dept', locale) || "Edit Department";
        if (mode === 'ADD_USER') return tc('organigram_ui.add_user', locale) || "Add User";
        if (mode === 'EDIT_USER') return tc('organigram_ui.edit_user', locale) || "Edit User";
        return "Edit";
    };

    if (!node) return null;

    return (
        <Drawer
            title={drawerTitle()}
            placement="right"
            width={500} // Fixed width or props.width
            onClose={onClose}
            open={visible}
            destroyOnClose
            maskClosable={true}
            styles={{ body: { paddingBottom: 80 } }}
            extra={
                mode !== 'EDIT_ORG' && (
                    <Button onClick={() => setMode('EDIT_ORG')}>
                        {tc('general.cancel', locale) || "Cancel"}
                    </Button>
                )
            }
        >
            {/* MODE: EDIT ORG */}
            {mode === 'EDIT_ORG' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <Form 
                        form={form}
                        layout="vertical" 
                        initialValues={node} 
                        onFinish={onSaveOrg}
                        key={node.id} // Re-mount form on node change
                    >
                        <Form.Item name="name" label={tc('organigram_ui.org_name', locale) || "Name"} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item 
                            name="externalId" 
                            label={tc('organigram_ui.external_id', locale) || "External ID"}
                            rules={[
                                { validator: (_, value) => checkUniqueOrgExtId(value, node?.id) ? Promise.resolve() : Promise.reject(new Error("ID Exists")) }
                            ]}
                        >
                            <Input />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>{tc('organigram_ui.save_details', locale) || "Save Details"}</Button>
                    </Form>
                    
                    <div style={{ borderTop: '1px solid #eee', paddingTop: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                            <h4>{tc('organigram_ui.users', locale) || "Users"}</h4>
                        </div>
                        
                        {['leaders', 'mitarbeiter', 'viewers'].map(role => (
                            <div key={role} style={{ marginBottom: 15 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0f2f5', padding: '5px 10px', borderRadius: 4 }}>
                                    <span style={{ textTransform: 'uppercase', fontSize: 11, fontWeight: 'bold' }}>
                                        {role === 'leaders' ? (tc('organigram_ui.role_leaders', locale) || "Leaders") : 
                                         role === 'mitarbeiter' ? (tc('organigram_ui.role_mitarbeiter', locale) || "Staff") : 
                                         (tc('organigram_ui.role_viewers', locale) || "Viewers")}
                                    </span>
                                    <Button size="small" type="text" icon={<PlusOutlined />} onClick={() => handleAddUserClick(role)} />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 5 }}>
                                    {(node.lists?.[role] || []).map(u => (
                                        <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #f0f0f0', padding: 5, borderRadius: 4 }}>
                                            <div>
                                                <div style={{ fontWeight: 'bold', fontSize: 12, color: (u.disabled || u.isDisabled) ? 'red' : 'inherit' }}>{u.firstName} {u.lastName}</div>
                                                <div style={{ fontSize: 10, color: '#888' }}>{u.jobTitle}</div>
                                            </div>
                                            <div>
                                                <Button size="small" type="text" icon={<EditOutlined />} onClick={() => handleEditUserClick(u)} />
                                                <Popconfirm 
                                                    title={tc('organigram_ui.remove_user_confirm', { locale, name: u.firstName + ' ' + u.lastName }) || `Remove ${u.firstName} ${u.lastName}?`}
                                                    onConfirm={() => onRemoveUser(u)}
                                                    okText="OK"
                                                    cancelText="Cancel"
                                                >
                                                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                                </Popconfirm>
                                            </div>
                                        </div>
                                    ))}
                                    {(!node.lists?.[role] || node.lists[role].length === 0) && <span style={{fontSize:11, color:'#999'}}>No users</span>}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Extra Actions (e.g. from Organigram) */}
                    {(onAddChildOrg || onDeleteOrg) && (
                        <div style={{ marginTop: 20, borderTop: '1px solid #eee', paddingTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {onAddChildOrg && (
                                <Button block icon={<PlusOutlined />} onClick={onAddChildOrg}>
                                    {tc('organigram_ui.add_sub_org', locale) || "Add Sub-Department"}
                                </Button>
                            )}
                            {onDeleteOrg && (
                                <Popconfirm 
                                    title={tc('organigram_ui.delete_org_confirm', locale) || "Delete this department?"}
                                    description={node.name}
                                    onConfirm={() => onDeleteOrg(node.id)}
                                    okText={tc('general.yes', locale) || "Yes"}
                                    cancelText={tc('general.no', locale) || "No"}
                                >
                                    <Button block danger icon={<DeleteOutlined />}>
                                        {tc('organigram_ui.delete_org', locale) || "Delete Department"}
                                    </Button>
                                </Popconfirm>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* MODE: ADD or EDIT USER */}
            {(mode === 'ADD_USER' || mode === 'EDIT_USER') && (
                <Form layout="vertical" form={userForm} onFinish={handleUserSubmit}>
                    
                    {mode === 'ADD_USER' && (
                         <Form.Item label="Type">
                             <Select value={addUserType} onChange={setAddUserType}>
                                 <Select.Option value="new">New User</Select.Option>
                                 <Select.Option value="existing">Existing User</Select.Option>
                             </Select>
                         </Form.Item>
                    )}

                    {mode === 'ADD_USER' && addUserType === 'existing' && (
                        <Form.Item name="existingUserId" label="Select User" rules={[{ required: true }]}>
                            <Select showSearch optionFilterProp="children">
                                {allUsers?.map(u => (
                                    <Select.Option key={u.id} value={u.id}>
                                        {u.firstName} {u.lastName} ({u.mail || u.email})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    {(mode === 'EDIT_USER' || (mode === 'ADD_USER' && addUserType === 'new')) && (
                        <>
                            <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="jobTitle" label="Job Title">
                                <Input />
                            </Form.Item>
                            <Form.Item name="mail" label="Email" rules={[
                                { type: 'email' },
                                { validator: (_, value) => (mode === 'EDIT_USER' && value === editingUser?.email) || checkUniqueEmail(value, editingUser?.id) ? Promise.resolve() : Promise.reject(new Error("Email Exists")) }
                            ]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="externalId" label="External ID" rules={[
                                { validator: (_, value) => (mode === 'EDIT_USER' && value === editingUser?.externalId) || checkUniqueUserExtId(value, editingUser?.id) ? Promise.resolve() : Promise.reject(new Error("ID Exists")) }
                            ]}>
                                <Input />
                            </Form.Item>
                        </>
                    )}
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                        {mode === 'ADD_USER' ? "Add User" : "Update User"}
                    </Button>
                </Form>
            )}
        </Drawer>
    );
};

export default DepartmentEditDrawer;
